import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { Music, Play, Pause, Clock, Trash2, Share2 } from 'lucide-react'
import { playlists } from '../api/client'
import { usePlayerStore } from '../stores/player'
import LikeButton from '../components/LikeButton'
import TrackMenu from '../components/TrackMenu'
import SharePlaylistModal from '../components/SharePlaylistModal'
import type { TrackWithDetails } from '../types'

function formatDuration(ms: number | null): string {
  if (!ms) return '--:--'
  const seconds = Math.floor(ms / 1000)
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function getTotalDuration(tracks: TrackWithDetails[]): string {
  const totalMs = tracks.reduce((sum, t) => sum + (t.duration_ms || 0), 0)
  const totalMins = Math.floor(totalMs / 1000 / 60)
  if (totalMins < 60) {
    return `${totalMins} min`
  }
  const hours = Math.floor(totalMins / 60)
  const mins = totalMins % 60
  return `${hours} hr ${mins} min`
}

export default function Playlist() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { playQueue, currentTrack, isPlaying } = usePlayerStore()
  const [showShareModal, setShowShareModal] = useState(false)

  const { data: playlist, isLoading } = useQuery({
    queryKey: ['playlists', id],
    queryFn: () => playlists.get(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => playlists.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playlists'] })
      navigate('/liked')
    },
  })


  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-dark-400">Loading...</div>
      </div>
    )
  }

  if (!playlist) {
    return (
      <div className="p-6">
        <div className="text-dark-400">Playlist not found</div>
      </div>
    )
  }

  const handlePlayAll = () => {
    if (playlist.tracks.length > 0) {
      playQueue(playlist.tracks, 0)
    }
  }

  const handlePlayTrack = (index: number) => {
    playQueue(playlist.tracks, index)
  }

  const handleDelete = () => {
    if (confirm(`Delete playlist "${playlist.name}"?`)) {
      deleteMutation.mutate()
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-end gap-6 mb-8">
        <div className="w-56 h-56 bg-gradient-to-br from-primary-600 to-primary-800 rounded-lg flex items-center justify-center flex-shrink-0 shadow-xl">
          <Music size={80} className="text-white" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-dark-400 uppercase tracking-wider mb-2">Playlist</p>
          <h1 className="text-4xl font-bold mb-2">{playlist.name}</h1>
          <div className="flex items-center gap-2 text-dark-300">
            <span>{playlist.tracks.length} songs</span>
            {playlist.tracks.length > 0 && (
              <>
                <span className="text-dark-500">•</span>
                <span>{getTotalDuration(playlist.tracks)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-4 mb-6">
        {playlist.tracks.length > 0 && (
          <button
            onClick={handlePlayAll}
            className="btn btn-primary flex items-center gap-2"
          >
            <Play size={18} />
            Play
          </button>
        )}
        <button
          onClick={() => setShowShareModal(true)}
          className="btn btn-secondary flex items-center gap-2"
        >
          <Share2 size={18} />
          Share
        </button>
        <button
          onClick={handleDelete}
          className="btn btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300"
        >
          <Trash2 size={18} />
          Delete Playlist
        </button>
      </div>

      {/* Share Modal */}
      {playlist && (
        <SharePlaylistModal
          playlist={{
            id: playlist.id,
            name: playlist.name,
            user_id: playlist.user_id,
            created_at: playlist.created_at,
            visibility: (playlist as any).visibility || 'private',
          }}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Track list */}
      {playlist.tracks.length > 0 ? (
        <div className="card">
          <table className="w-full">
            <thead>
              <tr className="text-left text-dark-400 text-sm border-b border-dark-700">
                <th className="py-3 px-4 w-12">#</th>
                <th className="py-3">Title</th>
                <th className="py-3">Artist</th>
                <th className="py-3">Album</th>
                <th className="py-3 px-4 w-20"></th>
                <th className="py-3 px-4 text-right">
                  <Clock size={16} />
                </th>
                <th className="py-3 px-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {playlist.tracks.map((track: TrackWithDetails, index: number) => {
                const isCurrentTrack = currentTrack?.id === track.id
                return (
                  <tr
                    key={track.id}
                    className={`group hover:bg-dark-700/50 cursor-pointer ${isCurrentTrack ? 'bg-dark-700/30' : ''}`}
                    onClick={() => handlePlayTrack(index)}
                  >
                    <td className="py-3 px-4">
                      {isCurrentTrack ? (
                        <span className="text-primary-400">
                          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                        </span>
                      ) : (
                        <>
                          <span className="text-dark-400 group-hover:hidden">{index + 1}</span>
                          <Play size={16} className="text-dark-100 hidden group-hover:block" />
                        </>
                      )}
                    </td>
                    <td className={`py-3 font-medium ${isCurrentTrack ? 'text-primary-400' : ''}`}>
                      {track.title}
                    </td>
                    <td className={`py-3 ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {track.artist_name || 'Unknown'}
                    </td>
                    <td className={`py-3 ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {track.album_title || 'Unknown'}
                    </td>
                    <td className="py-3 px-4">
                      <LikeButton trackId={track.id} />
                    </td>
                    <td className={`py-3 px-4 text-right ${isCurrentTrack ? 'text-primary-400/70' : 'text-dark-400'}`}>
                      {formatDuration(track.duration_ms)}
                    </td>
                    <td className="py-3 px-2">
                      <TrackMenu track={track} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card text-center py-12">
          <Music size={48} className="mx-auto text-dark-500 mb-4" />
          <p className="text-dark-400">This playlist is empty</p>
          <p className="text-dark-500 text-sm mt-2">
            Use the track menu to add songs to this playlist
          </p>
        </div>
      )}
    </div>
  )
}
